/**
 * Faces with ARKit blendshapes (2026-10-06): Luma's MetaPerson body carries the 51 standard
 * face shapes on her head (and the jaw ones on her lower teeth, blink on her eyelashes), so
 * her mouth is driven by those instead of the bone `jaw.ts` adds to the Mixamo characters.
 *
 * Lip sync: each of the 15 speech visemes is a mix of ARKit shapes (`VISEME_SHAPES`); the
 * face eases toward the mix for the sound being heard, so the mouth moves rather than jumps.
 */
import type * as THREE from 'three';
import type { Viseme } from '@clinspace/shared';
import type { MouthShape } from '../assistant/hostVoice.ts';
import type { Jaw } from './jaw.ts';

/** How far `jawOpen` goes at full speech when only the loudness is known (1 is a yawn). */
const SPEECH_OPEN = 0.55;
/** How quickly the mouth reaches a new shape (per second): about 60 ms to get most of the way. */
const LIP_RATE = 32;

const both = (base: string, w: number) => ({ [`${base}Left`]: w, [`${base}Right`]: w });

/**
 * The 15 visemes as ARKit shape weights, tuned for speech rather than for exaggerated
 * expressions: closed lips pressed (p b m), lower lip under the teeth (f v), rounded
 * (o, oo, w, sh), spread (ee, s), open (a).
 */
export const VISEME_SHAPES: Record<Viseme, Record<string, number>> = {
  sil: {},
  PP: { ...both('mouthPress', 0.45), mouthRollLower: 0.2, mouthRollUpper: 0.1, mouthClose: 0.1 },
  FF: { mouthRollLower: 0.55, ...both('mouthUpperUp', 0.25), jawOpen: 0.06 },
  TH: { jawOpen: 0.14, ...both('mouthStretch', 0.1), ...both('mouthUpperUp', 0.1) },
  DD: { jawOpen: 0.18, ...both('mouthStretch', 0.12) },
  kk: { jawOpen: 0.22, ...both('mouthStretch', 0.1) },
  CH: { jawOpen: 0.12, mouthFunnel: 0.45, mouthPucker: 0.2, ...both('mouthUpperUp', 0.15) },
  SS: { jawOpen: 0.07, ...both('mouthStretch', 0.3), ...both('mouthSmile', 0.12) },
  nn: { jawOpen: 0.13, ...both('mouthStretch', 0.08) },
  RR: { jawOpen: 0.12, mouthFunnel: 0.3, mouthPucker: 0.3 },
  aa: { jawOpen: 0.5, ...both('mouthLowerDown', 0.2) },
  E: { jawOpen: 0.3, ...both('mouthStretch', 0.3), ...both('mouthSmile', 0.1) },
  ih: { jawOpen: 0.18, ...both('mouthStretch', 0.35), ...both('mouthSmile', 0.2) },
  oh: { jawOpen: 0.38, mouthFunnel: 0.55, mouthPucker: 0.15 },
  ou: { jawOpen: 0.14, mouthPucker: 0.7, mouthFunnel: 0.3 },
};

/** Every shape lip sync moves, so a shape it stops using eases back to rest. */
const LIP_SHAPES = [...new Set(Object.values(VISEME_SHAPES).flatMap((s) => Object.keys(s)))];

/** The shape weights for a moment of speech: the sound now, leaning toward the next. */
export function mouthWeights(shape: MouthShape | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  for (const name of LIP_SHAPES) {
    if (!shape) out[name] = 0;
    else {
      const a = VISEME_SHAPES[shape.now][name] ?? 0;
      const b = VISEME_SHAPES[shape.next][name] ?? 0;
      out[name] = a + (b - a) * shape.toNext;
    }
  }
  return out;
}

export class ArkitFace {
  private readonly meshes: THREE.Mesh[] = [];
  private readonly lips = new Map<string, number>(LIP_SHAPES.map((n) => [n, 0]));

  constructor(root: THREE.Object3D) {
    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.isMesh && mesh.morphTargetDictionary && mesh.morphTargetInfluences)
        this.meshes.push(mesh);
    });
  }

  /** Whether any part of the face has this shape. */
  has(shape: string): boolean {
    return this.meshes.some((m) => shape in m.morphTargetDictionary!);
  }

  /** Sets a shape (0..1) on every mesh that has it: the head and the teeth move together. */
  set(shape: string, value: number): void {
    for (const m of this.meshes) {
      const i = m.morphTargetDictionary![shape];
      if (i !== undefined) m.morphTargetInfluences![i] = value;
    }
  }

  /**
   * Lip sync: eases the mouth toward the shape being spoken (`undefined`: back to rest).
   * Returns whether the mouth is still moving or open, so the caller knows lip sync owns it.
   */
  speak(shape: MouthShape | undefined, dt: number): boolean {
    const target = mouthWeights(shape);
    const k = 1 - Math.exp(-LIP_RATE * dt);
    let open = false;
    for (const [name, now] of this.lips) {
      const next = now + ((target[name] ?? 0) - now) * k;
      this.lips.set(name, next);
      this.set(name, next);
      if (next > 0.005) open = true;
    }
    return open;
  }

  /** The face's jaw as the `Jaw` the loudness driver expects (no timing available). */
  jaw(): Jaw | undefined {
    if (!this.has('jawOpen')) return undefined;
    return { set: (open) => this.set('jawOpen', Math.max(0, Math.min(1, open)) * SPEECH_OPEN) };
  }
}
